import { normalize, schema } from 'normalizr'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { currencySymbol, getCurrencyIcon } from '@last/core'
import type { LIconName } from '@last/core-ui/paprika'
import { useNotification } from '@last/core-ui/v3/plugins/notification'
import type { PaymentFrequency } from '@last/core/src/internal-billing'
import type { PlanName } from '@last/core/src/internal-billing/plans/locations/types'
import type { LastProduct } from '@last/core/src/lastProducts'

import api from '@/api.js'
import { internalBillingApi, locationApi } from '@/api/client'
import i18n from '@/i18n'
import type { CreateLocationRequest, UpdatePlanRequestPaymentFrequencyEnum } from '@/openapi'
import { useConfigStore } from '@/store/config'
import { useTrackingStore } from '@/store/tracking'
import type { LegalEntity, Location, Organization } from '@/types'

const locationSchema = new schema.Entity('locations')
const organizationSchema = new schema.Entity('organizations', {
  locations: [locationSchema]
})

export const useLocationsStore = defineStore('locations', () => {
  const { identify } = useTrackingStore()

  const notification = useNotification()

  const config = useConfigStore()
  const { refreshConfig, refreshBillingStatus } = config

  const { t } = i18n.global as any

  const organizationIds = ref<string[]>([])
  const organizations = ref<Record<string, Organization>>({})
  const locations = ref<Record<string, Location>>({})
  const selectedLocationId = ref<string | undefined>(
    localStorage.getItem('selectedLocationId') || undefined
  )
  const selectedOrganizationId = ref<string | undefined>(
    localStorage.getItem('selectedOrganizationId') !== 'undefined'
      ? localStorage.getItem('selectedOrganizationId') || undefined
      : undefined
  )

  const locationList = computed<Location[]>(() => Object.values(locations.value))

  const userHasSingleLocation = computed<boolean>(
    () =>
      Object.values(organizations.value).reduce(
        (total, organization) => total + organization.totalLocations,
        0
      ) === 1
  )

  const isSingleLocation = computed<boolean>(() => {
    return organizations.value[selectedOrganizationId.value as string]?.totalLocations === 1
  })

  const isLocationMode = computed<boolean>(() => {
    return !!selectedLocationId.value && !isSingleLocation.value
  })

  const organizationList = computed<Organization[]>(() =>
    organizationIds.value.map(id => ({
      ...organizations.value[id],
      locations: organizations.value[id].locations.map(
        locationId => locations.value[locationId as unknown as string]
      )
    }))
  )

  const selectedLocation = computed<Location | undefined>(() =>
    selectedLocationId.value ? locations.value[selectedLocationId.value] : undefined
  )

  const selectedOrganization = computed<Organization | undefined>(() => {
    if (!selectedOrganizationId.value) return undefined

    const organization = organizations.value[selectedOrganizationId.value]
    if (!organization) return undefined

    return {
      ...organization,
      locations: (organization?.locations || []).map(
        locationId => locations.value[locationId as unknown as string]
      )
    }
  })

  const currencyIcon = computed<string>(() => {
    return currencySymbol(
      selectedLocation.value?.currencyCode || Object.values(locations.value)[0]?.currencyCode
    )
  })

  const currencyLIcon = computed<LIconName>(() => {
    return getCurrencyIcon(
      selectedLocation.value?.currencyCode || Object.values(locations.value)[0]?.currencyCode
    )
  })

  const lastProducts = computed<{
    location: Record<LastProduct, boolean>
    organization: Record<LastProduct, boolean>
  }>(
    (): {
      location: Record<LastProduct, boolean>
      organization: Record<LastProduct, boolean>
    } => {
      if (!selectedOrganizationId.value)
        return {
          location: {} as Record<LastProduct, boolean>,
          organization: {} as Record<LastProduct, boolean>
        }

      const organizationProducts: Record<LastProduct, boolean> = {} as Record<LastProduct, boolean>
      let locationProducts: Record<LastProduct, boolean> = {} as Record<LastProduct, boolean>

      locationList.value.forEach(location => {
        Object.keys(location.lastProducts).forEach(product => {
          organizationProducts[product as LastProduct] =
            organizationProducts[product as LastProduct] ||
            location.lastProducts[product as LastProduct]
        })
      })

      if (selectedLocation.value) locationProducts = selectedLocation.value.lastProducts || {}

      return {
        location: { ...locationProducts },
        organization: { ...organizationProducts }
      }
    }
  )

  async function refreshLocations(): Promise<void> {
    const response = await api.get('/dashboard/locations')
    const normalizedOrganizations = normalize(response.data, [organizationSchema])
    locations.value = normalizedOrganizations.entities.locations || {}
    organizations.value = normalizedOrganizations.entities.organizations || {}
    organizationIds.value = normalizedOrganizations.result

    if (!selectedOrganizationId.value) {
      const organization = organizations.value[organizationIds.value[0]]
      selectedOrganizationId.value = organization?.id
      if (selectedOrganizationId.value)
        localStorage.setItem('selectedOrganizationId', selectedOrganizationId.value)
    }

    if (
      !selectedLocationId.value &&
      (isSingleLocation.value ||
        (!isSingleLocation.value && Object.keys(locations.value).length === 1))
    ) {
      selectedLocationId.value = Object.values(locations.value)[0]?.id
      if (selectedLocationId.value)
        localStorage.setItem('selectedLocationId', selectedLocationId.value)

      identify()
    }
  }

  async function setOrganizationAndLocationIds(
    organizationId: string,
    locationId?: string
  ): Promise<void> {
    if (!locationId) {
      removeSelectedLocation()
      selectedOrganizationId.value = organizationId
      localStorage.setItem('selectedOrganizationId', organizationId)
    } else {
      if (!locations.value[locationId]) {
        await refreshLocations()
      }

      selectedOrganizationId.value = locations.value[locationId].organizationId
      localStorage.setItem('selectedOrganizationId', selectedOrganizationId.value)
      await refreshConfig()
      await refreshBillingStatus()
      selectedLocationId.value = locationId
      localStorage.setItem('selectedLocationId', selectedLocationId.value)
    }

    await refreshConfig()
    await refreshBillingStatus()

    identify()
  }

  async function setSelectedLocationId(locationId: string): Promise<void> {
    if (!locations.value[locationId]) {
      await refreshLocations()
    }

    if (selectedLocationId.value !== locationId) {
      selectedOrganizationId.value = locations.value[locationId].organizationId
      localStorage.setItem('selectedOrganizationId', selectedOrganizationId.value)
      selectedLocationId.value = locationId
      localStorage.setItem('selectedLocationId', selectedLocationId.value)

      await refreshConfig()
      await refreshBillingStatus()

      identify()
    }
  }

  async function setSelectedOrganizationId(organizationId: string): Promise<void> {
    removeSelectedLocation()
    if (selectedOrganizationId.value !== organizationId) {
      selectedOrganizationId.value = organizationId
      localStorage.setItem('selectedOrganizationId', organizationId)
      await refreshConfig()
      await refreshBillingStatus()
    }
    identify()
  }

  function removeSelectedLocation(): void {
    selectedLocationId.value = undefined
    localStorage.removeItem('selectedLocationId')
  }

  function removeSelectedOrganization(): void {
    selectedOrganizationId.value = undefined
    localStorage.removeItem('selectedOrganizationId')
  }

  async function setSelectedLocationIdByQueryParam(locationId: string): Promise<void> {
    selectedLocationId.value = locationId
    localStorage.setItem('selectedLocationId', locationId)
  }

  async function updateLocationPlan({
    locationId,
    plan,
    paymentFrequency
  }: {
    locationId?: string
    plan: PlanName
    paymentFrequency?: PaymentFrequency
  }): Promise<{ updated: boolean }> {
    const locId = locationId ?? selectedLocationId.value

    if (!locId) {
      throw new Error('No location ID provided')
    }

    await internalBillingApi.updatePlan({
      updatePlanRequest: {
        plan,
        paymentFrequency: paymentFrequency as UpdatePlanRequestPaymentFrequencyEnum,
        locationId: locId
      }
    })

    const { data: subscription } = await api.get('/dashboard/subscriptions/location')

    locations.value[locId].plan = subscription.plan

    if (subscription.plan === plan) return { updated: true }
    return { updated: false }
  }

  async function updateLocationLastProduct(product: LastProduct, active: boolean): Promise<void> {
    if (!selectedLocation.value || !selectedLocationId.value) {
      throw new Error('No location selected')
    }

    selectedLocation.value.lastProducts[product] = active

    await api.put('/dashboard/last-product', {
      locationId: selectedLocationId.value,
      product,
      active
    })
  }

  async function oldCreateNewLocation(data: Partial<Location>): Promise<void> {
    const { plan, ...payload } = data
    await api.post('/dashboard/locations', {
      ...payload,
      plan: { code: plan!.plan, paymentFrequency: plan!.frequency }
    })
    await refreshLocations()
  }

  async function createNewLocation(data: CreateLocationRequest): Promise<string> {
    const locationId = await locationApi.createLocation({ createLocationRequest: data })

    await refreshLocations()
    await refreshConfig()

    return locationId
  }

  async function updateLocation(location: Location & { newLegalEntity?: LegalEntity }) {
    try {
      await api.put(`/dashboard/locations/${location.id}`, {
        location
      })
    } catch (e: any) {
      notification.create({
        icon: 'close',
        iconColor: 'red',
        title: e?.response?.data.error || t('locations.error-updating-location')
      })
    }

    await refreshLocations()
    await refreshConfig()
  }

  function clear(): void {
    organizationIds.value = []
    organizations.value = {}
    locations.value = {}
    selectedLocationId.value = undefined
    selectedOrganizationId.value = undefined
  }

  return {
    organizationIds,
    organizations,
    locations,
    selectedLocationId,
    selectedOrganizationId,

    locationList,
    userHasSingleLocation,
    isSingleLocation,
    isLocationMode,
    organizationList,
    selectedLocation,
    selectedOrganization,
    currencyIcon,
    currencyLIcon,
    lastProducts,

    refreshLocations,
    setOrganizationAndLocationIds,
    setSelectedLocationId,
    setSelectedOrganizationId,
    removeSelectedLocation,
    removeSelectedOrganization,
    setSelectedLocationIdByQueryParam,
    updateLocationPlan,
    updateLocationLastProduct,
    updateLocation,
    oldCreateNewLocation,
    createNewLocation,
    clear
  }
})
